(ns graph-ui.web.mantine-generic
  "Render ANY generated widget kind by driving the Mantine component the manifest
   says it is — F-graph-ui-v0-006-01, the renderer half.

   @feature F-graph-ui-v0-006-01-mantine-vocabulary-generator
   @adr ADR-graph-ui-v0-001-01-adopt-mantine-whole
   @adr ADR-graph-ui-v0-012-01-mantine-component-library
   @adr ADR-graph-ui-v0-013-01-one-layout-model

   WHY THIS IS ONE FUNCTION AND NOT 77. The vocabulary is now GENERATED from
   @mantine/core's own declarations, so a generated kind is Mantine's component
   with Mantine's prop names, kebab-cased. There is nothing left for a
   hand-written adapter to decide — `{:widget :divider :orientation :vertical}`
   is `<Divider orientation=\"vertical\" />` by construction. Writing 77 adapters
   would be re-deriving, by hand and fallibly, a mapping the manifest already
   states. It would also go stale the moment Mantine adds a prop, which is the
   precise failure the generator exists to end.

   So this reads the SAME data the manifest holds — via the committed
   graph-ui.web.generated-meta-ui mirror — and uses it three ways:
     :mantine :component   which React component to render
     :props                which props are legal (so nothing else reaches the DOM)
     :slots / :gestures    which children are NAMED SLOTS, and which handlers exist

   WHAT IT DELIBERATELY DOES NOT DO. It does not bind data sources, own an edit
   buffer, or manage selection. A kind that needs any of those graduates to a
   hand-written widget and an explicit case in render.cljs — which is exactly what
   :tree, :table, :text-area and the other 33 already are. This is the floor that
   makes every kind DRAW, not a claim that every kind is finished."
  (:require [clojure.string :as str]
            [reagent.core :as r]
            [goog.object :as gobj]
            ["@mantine/core" :as mantine]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.generated-meta-ui :as generated]))

;; ---------------------------------------------------------------------------
;; The manifest, read as data.
;; ---------------------------------------------------------------------------

(defn- malli-map-keys
  "The prop names a kind declares, read off its malli [:map [:k opts schema] …].
   Reading the schema rather than keeping a second list is the whole point: a prop
   added by the next Mantine release becomes legal here with no code change."
  [schema]
  (when (vector? schema)
    (into #{}
          (comp (filter vector?) (map first) (filter keyword?))
          (rest schema))))

(def ^:private rules
  "Every version's :rules merged, newest last. Generated kinds live only in the
   open frontier, and hand-written kinds never reach this namespace (render.cljs
   dispatches them explicitly first), so a flat merge is unambiguous here."
  (delay
    (->> (:versions generated/meta-ui-versions)
         (sort-by key)
         (map (comp :rules val))
         (reduce merge {}))))

(defn- rule [kind] (get @rules kind))

(defn component-name
  "The Mantine component this kind is, or nil if the kind is not Mantine-backed.
   Stamped by the generator as :mantine {:component \"Divider\"}."
  [kind]
  (get-in (rule kind) [:mantine :component]))

(defn component-for
  "The React component itself, looked up on the npm module by the name the
   manifest recorded. nil when Mantine does not export it under that name —
   returning nil rather than throwing so a bad row in the manifest degrades to
   the visible `unknown widget` placeholder instead of blanking the page."
  [kind]
  (when-let [n (component-name kind)]
    (gobj/get mantine n)))

;; ---------------------------------------------------------------------------
;; Names and values.
;; ---------------------------------------------------------------------------

(defn ->camel
  "Kebab keyword -> the camelCase name Mantine declares. :label-position ->
   \"labelPosition\". The generator produced these keywords BY kebab-casing
   Mantine's names, so this is that function inverted, and the round-trip is
   exact for every name in the package (asserted in mantine-generic-test)."
  [k]
  (let [[h & t] (str/split (name k) #"-")]
    (apply str h (map str/capitalize t))))

(defn ->js-value
  "A description value -> what Mantine expects. Keywords are how EDN spells the
   string enums Mantine's types are made of (:vertical -> \"vertical\"); numbers,
   strings and booleans pass through; nested data (a gradient, a breakpoint map)
   becomes a plain JS object with camelCase keys."
  [v]
  (cond
    (keyword? v) (name v)
    (map? v)     (clj->js (into {} (map (fn [[k x]] [(->camel k) (->js-value x)])) v))
    (vector? v)  (clj->js (mapv ->js-value v))
    :else        v))

;; ---------------------------------------------------------------------------
;; Rendering.
;; ---------------------------------------------------------------------------

(defn- gesture-handlers
  "This node's :on-gesture map -> Mantine handler props. ADR-graph-ui-v0-013-01
   §7-10 keys gestures by name and the generator took those names from Mantine's
   own handlers, so :change is `onChange` — the inverse of how it was extracted.
   Only gestures the KIND declares are wired; an undeclared one is ignored rather
   than sprayed onto the DOM as an unknown attribute."
  [kind props bus session-id]
  (let [declared (set (:gestures (rule kind)))]
    (into {}
          (for [[g template] (:on-gesture props)
                :when (and bus (contains? declared g))]
            [(->camel (keyword (str "on-" (name g))))
             (fn [& _]
               (js-invoke bus "dispatch"
                          (bind/fill-command template {'$session-id session-id})
                          (fn [_] nil)))]))))

(defn- r-fragment [hiccups]
  (if (= 1 (count hiccups))
    (first hiccups)
    (into [:<>] hiccups)))

(defn- split-slots
  "Children -> [named-slot-props remaining-children]. A child says which slot of
   its parent it fills with :section — the SAME universal slot :app-shell already
   uses, generalised, because Mantine's `leftSection`/`label` props are the same
   idea: a place a widget goes. No second mechanism was invented for it.

   THE LEFTOVERS ARE NOT ALWAYS BODY CONTENT. Under `:children :slots` the
   component has no body — `Divider` takes no children, its label is a prop — so
   a child that names no slot, or names one this kind does not declare, has
   nowhere to go. ui.interpret cannot reject it: whether a child is misplaced is
   a fact about its PARENT, and that schema dispatches on the child's own
   :widget. Dropping it silently would leave an author looking at a description
   that plainly says something and a screen that plainly does not, which is the
   silent contract this project has a concept doc about
   (CONCEPT-ikc-v3-002-01-silent-contract). So widget-generic draws them, in
   place and in red."
  [kind children render-child]
  (let [declared (set (:slots (rule kind)))
        grouped  (group-by #(let [s (get-in % [:node :props :section])]
                              (if (contains? declared s) s ::rest))
                           children)]
    [(into {} (for [[slot xs] grouped
                    :when (not= ::rest slot)]
                ;; as-element, not raw hiccup: this value becomes a React PROP,
                ;; and Reagent only interprets hiccup it finds in child position.
                [(->camel slot) (r/as-element (r-fragment (map render-child xs)))]))
     (map render-child (::rest grouped))
     ;; The leftovers' own nodes, so a caller that has no body to put them in can
     ;; name what it could not place. Kept separate from the rendered hiccup
     ;; because the message wants the child's KIND and :section, not its markup.
     (map :node (::rest grouped))]))

(def ^:private error-boundary
  "A React error boundary, so a component that rejects what a description gave it
   degrades to a visible, named box instead of unmounting the whole island.

   This is not belt-and-braces. Several Mantine components throw rather than
   render when a required input is missing — `Tooltip` calls
   `React.Children.only`, `CopyButton` CALLS its children — and a description is
   authored data that can always be wrong. Without this, one bad node blanks the
   page, which is the failure mode ADR-graph-ui-v0-001-01 already paid for once."
  (r/create-class
   {:display-name "GenericWidgetBoundary"
    :get-initial-state (fn [] #js {:error nil})
    :component-did-catch (fn [_ _ _] nil)
    :get-derived-state-from-error (fn [e] #js {:error (str (.-message e))})
    :reagent-render
    (fn [kind & children]
      (let [this (r/current-component)
            err  (gobj/get (.-state this) "error")]
        (if err
          [:div.ikc-fg-crit (str "widget " kind " could not render: " err)]
          (into [:<>] children))))}))

(defn widget-generic
  "Render `kind` as its Mantine component. `children` is a seq of
   {:node <render-tree node> :hiccup <already-rendered hiccup>} so named slots can
   be routed by the child's own :section before anything is placed."
  [kind props children bus session-id]
  (if-let [comp (component-for kind)]
    (let [r         (rule kind)
          declared  (malli-map-keys (:props r))
          js-props  (into {} (for [[k v] props
                                   :when (contains? declared k)]
                               [(->camel k) (->js-value v)]))
          handlers  (gesture-handlers kind props bus session-id)
          [slots kids unplaced] (split-slots kind children :hiccup)
          takes-kids? (= :any (:children r))
          ;; A RENDER PROP is not children. CopyButton/FileButton CALL
          ;; `children(payload)`, so handing them elements throws. The description
          ;; still nests normally; only this line knows the difference.
          render-prop? (= :function (get-in r [:mantine :children]))
          body      (merge js-props handlers slots)]
      [error-boundary kind
       (if render-prop?
         ;; createElement rather than [:> …]: the function must arrive as the
         ;; CHILD, and Reagent reads a function in child position as a component
         ;; to invoke, not as a value to pass through.
         (r/create-element comp (clj->js body)
                           (fn [& _] (r/as-element (r-fragment kids))))
         (into [:> comp body] (when takes-kids? kids)))
       ;; A child this kind has nowhere to put — see split-slots. Named rather
       ;; than dropped, and drawn AFTER the component so it cannot disturb what
       ;; did render. Empty in every correct description, so it costs nothing.
       (when (and (not takes-kids?) (seq unplaced))
         (into [:div.ikc-fg-crit]
               (for [n unplaced]
                 [:div (str "widget " kind " has no place for a "
                            (:type n) " child"
                            (if-let [s (get-in n [:props :section])]
                              (str " asking for slot " s " — this kind declares "
                                   (or (some-> (:slots r) vec) "no slots"))
                              (str " with no :section — this kind holds children "
                                   "only in its named slots "
                                   (or (some-> (:slots r) vec) "[]"))))]))) ])
    ;; Not Mantine-backed and not hand-written: say so, loudly and in place.
    [:div.ikc-fg-crit (str "unknown widget: " kind)]))
